import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { PlusCircle, Calendar, Clock, UserCheck, CheckCircle2, DollarSign, ArrowRight, Users, Wallet, Library, CalendarDays, Check, X } from "lucide-react";
import { api, apiError, formatDate, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import EventCard from "@/components/EventCard";
import { EmptyState } from "@/components/common";
import { CalendarX2 } from "lucide-react";

const TONE = { amber: "text-primary", orange: "text-accent", emerald: "text-emerald-400", red: "text-red-400", sky: "text-sky-400" };

function Stat({ label, value, icon: Icon, tone = "amber", onClick, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className="text-left bg-card border border-border rounded-2xl p-4 sm:p-5 min-h-[92px] hover:border-primary/40 hover:shadow-[0_8px_30px_rgba(0,0,0,0.25)] transition-all active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background">
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold leading-tight">{label}</span>
        <span className={`flex h-8 w-8 items-center justify-center rounded-lg bg-white/5 shrink-0 ${TONE[tone]}`}><Icon className="h-4 w-4" /></span>
      </div>
      <div className={`font-mono text-2xl sm:text-3xl font-bold mt-2 ${TONE[tone]}`}>{value}</div>
    </button>
  );
}

const SHORTCUTS = [
  { to: "/eventos?tab=mine", label: "Meus eventos", icon: CalendarDays },
  { to: "/agenda", label: "Agenda", icon: Clock },
  { to: "/repertorio", label: "Repertório", icon: Library },
  { to: "/pagamentos", label: "Financeiro", icon: Wallet },
];

export default function CreatorDashboard({ d, reload, embedded }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const firstName = (user?.stage_name || "").split(" ")[0] || "";
  const c = d.creator || {};
  const applicants = c.applicants_preview || [];

  const decide = async (aid, status) => {
    try {
      await api.put(`/applications/${aid}/status`, { status });
      toast.success(status === "aprovado" ? "Candidato aprovado!" : "Candidato recusado.");
      reload();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-7 fade-up" data-testid="creator-dashboard">
      {!embedded && (
      <section className="relative overflow-hidden rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/10 via-card to-card p-5 sm:p-8">
        <div className="pointer-events-none absolute -right-12 -top-12 h-48 w-48 rounded-full bg-primary/10 blur-3xl" aria-hidden="true" />
        <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="min-w-0">
            {firstName && <p className="text-sm text-muted-foreground">Olá, <span className="text-foreground font-semibold">{firstName}</span> 👋</p>}
            <h1 className="font-head text-3xl sm:text-4xl font-extrabold tracking-tight mt-1 leading-tight">
              Pronto para <span className="bg-gradient-to-r from-primary to-amber-400 bg-clip-text text-transparent">montar seu próximo evento?</span>
            </h1>
            <p className="text-muted-foreground mt-2 text-sm sm:text-base">Crie, gerencie sua equipe e acompanhe tudo em um só lugar.</p>
          </div>
          <Button data-testid="creator-primary-cta-create-event" onClick={() => navigate("/eventos/novo")} size="lg" className="gap-2 shrink-0 shadow-[0_0_20px_hsl(var(--primary)/0.35)]">
            <PlusCircle className="h-5 w-5" /> Criar evento
          </Button>
        </div>
      </section>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3 sm:gap-4">
        <Stat testid="stat-card-active-events" label="Eventos ativos" value={c.active_events || 0} icon={Calendar} tone="amber" onClick={() => navigate("/eventos?tab=mine")} />
        <Stat testid="stat-card-upcoming-events" label="Próximos eventos" value={c.upcoming_events || 0} icon={Clock} tone="amber" onClick={() => navigate("/agenda")} />
        <Stat testid="stat-card-pending-applicants" label="Candidatos aguardando" value={c.pending_applicants || 0} icon={UserCheck} tone="orange" onClick={() => document.querySelector('[data-testid="creator-candidate-review-section"]')?.scrollIntoView({ behavior: "smooth", block: "start" })} />
        <Stat testid="stat-card-confirmed-musicians" label="Músicos confirmados" value={c.confirmed_musicians || 0} icon={CheckCircle2} tone="emerald" onClick={() => navigate("/eventos?tab=mine")} />
        <Stat testid="stat-card-pending-payments" label="Pagamentos pendentes" value={c.pending_payments || 0} icon={DollarSign} tone="red" onClick={() => navigate("/pagamentos")} />
      </div>

      {c.finance && (
        <div data-testid="creator-finance-summary" className="grid grid-cols-2 gap-3 sm:gap-4">
          <button onClick={() => navigate("/pagamentos")} className="text-left rounded-2xl border border-red-500/25 bg-red-500/[0.06] p-4 sm:p-5 hover:border-red-500/40 transition-all active:scale-[0.98]">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold"><DollarSign className="h-4 w-4 text-red-400" />A pagar este mês</div>
            <div className="font-mono text-2xl sm:text-3xl font-bold mt-2 text-red-400" data-testid="finance-to-pay">{money(c.finance.to_pay)}</div>
          </button>
          <button onClick={() => navigate("/pagamentos")} className="text-left rounded-2xl border border-emerald-500/25 bg-emerald-500/[0.06] p-4 sm:p-5 hover:border-emerald-500/40 transition-all active:scale-[0.98]">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold"><CheckCircle2 className="h-4 w-4 text-emerald-400" />Já pago este mês</div>
            <div className="font-mono text-2xl sm:text-3xl font-bold mt-2 text-emerald-400" data-testid="finance-paid">{money(c.finance.paid)}</div>
          </button>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {SHORTCUTS.map((s) => (
          <button key={s.to} data-testid={`creator-shortcut-${s.label.toLowerCase().replace(/\s/g, "-")}`} onClick={() => navigate(s.to)}
            className="group flex flex-col items-center justify-center gap-2 rounded-2xl border border-border bg-card p-4 min-h-[96px] hover:border-primary/40 hover:bg-surface-hover transition-all active:scale-[0.98]">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary group-hover:bg-primary/20 transition-colors"><s.icon className="h-5 w-5" /></span>
            <span className="text-sm font-semibold text-center">{s.label}</span>
          </button>
        ))}
      </div>

      <section data-testid="creator-candidate-review-section">
        <h2 className="font-head text-xl sm:text-2xl font-semibold tracking-tight mb-4">Candidatos aguardando análise</h2>
        {applicants.length === 0 ? (
          <EmptyState icon={UserCheck} description="Nenhum candidato aguardando. Crie um evento para começar a receber candidaturas." />
        ) : (
          <div className="space-y-2">
            {applicants.map((a) => (
              <div key={a.app_id} data-testid={`applicant-${a.app_id}`} className="flex items-center gap-3 bg-card border border-accent/30 rounded-2xl px-4 py-3">
                <div className="h-11 w-11 rounded-full bg-[#1C1C24] overflow-hidden flex items-center justify-center text-sm font-bold shrink-0">
                  {a.photo ? <img src={a.photo} alt="" className="h-full w-full object-cover" /> : (a.stage_name?.[0] || "?")}
                </div>
                <button onClick={() => navigate(`/eventos/${a.event_id}`)} className="min-w-0 flex-1 text-left">
                  <div className="font-medium truncate">{a.stage_name || "Músico"}{a.instrument ? <span className="text-muted-foreground font-normal"> · {a.instrument}</span> : null}</div>
                  <div className="text-xs text-muted-foreground truncate">{a.event_name}</div>
                </button>
                <div className="flex gap-2 shrink-0">
                  <Button data-testid={`btn-approve-candidate-${a.app_id}`} onClick={() => decide(a.app_id, "aprovado")} size="sm" className="bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 gap-1 h-9"><Check className="h-4 w-4" />Aprovar</Button>
                  <Button data-testid={`btn-reject-candidate-${a.app_id}`} onClick={() => decide(a.app_id, "reprovado")} size="sm" variant="ghost" className="text-red-400 hover:bg-red-500/15 gap-1 h-9"><X className="h-4 w-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-head text-xl sm:text-2xl font-semibold tracking-tight">Meus próximos eventos</h2>
          <button onClick={() => navigate("/eventos?tab=mine")} className="text-sm text-accent flex items-center gap-1 hover:gap-2 transition-all">Ver todos <ArrowRight className="h-4 w-4" /></button>
        </div>
        {(c.upcoming_events_list || []).length === 0 ? (
          <EmptyState icon={CalendarX2} description="Você ainda não tem eventos a caminho." />
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">{c.upcoming_events_list.map((ev, i) => <EventCard key={ev.id} ev={ev} index={i} />)}</div>
        )}
      </section>
    </div>
  );
}
